/**
 * O motor CPTu — a porta de entrada (spec 07, RN-252).
 *
 * Um motor TypeScript puro, sem dependência, sem rede, sem banco e sem tela,
 * idêntico byte a byte em `api/`, `web/` e `portal-web/` (o `298.90` confere).
 * Quem usa o motor chama `processarVertical` (ou `processarCampanha`, para as
 * verticais de uma campanha) e recebe um `Resultado`: uma coluna por grandeza,
 * as quatro classificações, as leituras que a edição tirou e os avisos.
 *
 * A ordem é sempre a mesma: a profundidade inválida sai primeiro, depois a
 * edição (`edicao.ts`), o básico (`basico.ts`), a classificação
 * (`classificacao.ts`) e as correlações (`correlacoes.ts`). A dissipação
 * (`dissipacao.ts`), a correlação cruzada (`correlacaoCruzada.ts`) e as camadas
 * (`camadas.ts`: as sugestões, a estatística por camada e o perfil médio — sugestão,
 * fora do RN-252, onda 313) e o espacial (`espacial.ts`: o variograma, o IDW, a
 * krigagem, a seção e a grade de voxels — modelo interpretativo, fora do RN-252, onda
 * 316) são contas à parte, que a tela chama quando precisa. O
 * que cada coluna vale contra o gabarito 2609350 — e o que fica fora do PDF e do
 * Portal — está em
 * `conformidade.ts`: `grandezaHomologada(coluna, premissas)` para as colunas do
 * `Resultado` (com as premissas, também as condições da homologação) e
 * `dissipacaoHomologada(saida)` para o t₅₀, o Ir, o cₕ e o kₕ da dissipação.
 *
 * Algumas exportações são de DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL (abaixo, com
 * o porquê): elas servem aos testes e às ferramentas para comparar com o CPeT-IT
 * e com as fontes, não ao cálculo que vai ao PDF e ao Portal.
 */
import { calcularBasico } from './basico';
import { classificar } from './classificacao';
import { condicoesAtendidas } from './conformidade';
import { correlacionar } from './correlacoes';
import { aplicarEdicao } from './edicao';
import type { Edicao, LeituraRemovida } from './edicao';
import type { Grandeza, Leituras, Premissas, Resultado, Serie } from './tipos';
import { premissasEfetivas } from './tipos';
import { MOTOR_CPTU_VERSAO } from './versao';

export * from './versao';
export * from './tipos';
export * from './unidades';
export * from './edicao';
export * from './basico';
export * from './classificacao';
export * from './correlacoes';
export * from './dissipacao';
export * from './correlacaoCruzada';
export * from './conformidade';
export * from './camadas';
export * from './espacial';

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: a média móvel do básico, exportada para comparar os canais
 * suavizados; `calcularBasico` (e `processarVertical`) já a aplica.
 */
export { mediaMovelDoBasico } from './basico';

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: as zonas SBT e SBTn por fronteiras DADAS (o
 * `ferramentas/cptu-abacos/refinar.mjs` passa as do desenho puro); quem classifica é `zonaSbtn` e
 * `zonaSbt`, com `FRONTEIRAS_ROBERTSON`.
 */
export { zonaSbtnCom, zonaSbtCom } from './classificacao';

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: os coeficientes do Kc como impressos no artigo, para
 * comparar; o Kc do motor usa `KC_ROBERTSON_2022`.
 */
export { KC_ROBERTSON_2022_IMPRESSO } from './correlacoes';

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: as premissas do registro velho do CPeT-IT para o CPTu01
 * (γ fixo e NA 0), só para comparar; o relatório usa as da própria vertical (spec §5.5).
 */
export { premissasDaDissipacaoDoCPeTIT } from './dissipacao';

/** O motivo da leitura que sai antes de tudo por não ter profundidade (NaN ou infinita). */
export const MOTIVO_PROFUNDIDADE_INVALIDA = 'profundidade inválida';

/** Quantos trechos de u₂ negativo os avisos escrevem um a um (os mais extensos); o resto vai contado. */
const TRECHOS_ESCRITOS = 5;

/** Dois trechos de u₂ negativo separados por até isto, em m, são um trecho só. */
const JUNTA_DOS_TRECHOS = 0.05;

/** Folga de ponto flutuante nas comparações de profundidade. */
const FOLGA = 1e-9;

/** Quantas profundidades o aviso de leitura incompleta escreve. */
const PROFUNDIDADES_ESCRITAS = 5;

/** 1.234 — o inteiro com o ponto dos milhares. */
const inteiro = (n: number): string => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '.');

/** 11,62 — a profundidade com duas casas e vírgula. */
const metros = (z: number): string => z.toFixed(2).replace('.', ',');

/** «1 leitura removida» ou «12 leituras removidas». */
const leiturasRemovidas = (n: number): string => (n === 1 ? '1 leitura removida' : `${inteiro(n)} leituras removidas`);

/** «a, b e c». */
function lista(itens: string[]): string {
  return itens.length <= 1 ? itens.join('') : `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** A série nas posições dadas, em série nova. */
function escolher(s: Serie, indices: number[]): Serie {
  const saida = new Float64Array(indices.length);
  for (let k = 0; k < indices.length; k++) saida[k] = s[indices[k]];
  return saida;
}

/** Um canal que chegou: tem tamanho. */
const temTamanho = (s: unknown): s is ArrayLike<number> =>
  typeof s === 'object' && s !== null && typeof (s as { length?: unknown }).length === 'number';

/**
 * Um canal do equipamento com `n` leituras, em série nova: o que não é número finito (um infinito, o
 * que falta no fim de um canal mais curto) é leitura que falta, NaN. Canal ausente: tudo NaN.
 */
function canal(s: unknown, n: number): Serie {
  const saida = new Float64Array(n).fill(NaN);
  if (!temTamanho(s)) return saida;
  const ate = Math.min(n, s.length);
  for (let i = 0; i < ate; i++) {
    const v = s[i];
    if (typeof v === 'number' && Number.isFinite(v)) saida[i] = v;
  }
  return saida;
}

/** O nome de cada canal nos avisos. */
const NOME_DO_CANAL: Record<'profundidade' | 'qc' | 'fs' | 'u2', string> = {
  profundidade: 'profundidade', qc: 'qc', fs: 'fs', u2: 'u₂',
};

/**
 * As leituras como o motor as aceita, e os avisos do que chegou torto: canal ausente (vira NaN em
 * toda leitura), canal mais curto que a profundidade (o que falta é NaN), valor que não é número
 * finito (NaN). A entrada não muda; nada lança.
 */
function lerCanais(bruto: Leituras): { leituras: Leituras; avisos: string[] } {
  const entrada = (bruto ?? {}) as Partial<Leituras>;
  const avisos: string[] = [];
  if (!temTamanho(entrada.profundidade)) avisos.push(`canal ${NOME_DO_CANAL.profundidade} ausente`);
  const n = temTamanho(entrada.profundidade) ? entrada.profundidade.length : 0;
  for (const nome of ['qc', 'fs', 'u2'] as const) {
    const s = entrada[nome];
    if (!temTamanho(s)) avisos.push(`canal ${NOME_DO_CANAL[nome]} ausente`);
    else if (s.length < n) {
      avisos.push(`canal ${NOME_DO_CANAL[nome]} mais curto que a profundidade: ${inteiro(s.length)} de ${inteiro(n)} leituras`);
    }
  }
  // A profundidade não infinita fica como veio (a que não é número sai no passo 0, com motivo).
  const profundidade = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const z = (entrada.profundidade as ArrayLike<number>)[i];
    profundidade[i] = typeof z === 'number' && Number.isFinite(z) ? z : NaN;
  }
  const leituras: Leituras = {
    profundidade, qc: canal(entrada.qc, n), fs: canal(entrada.fs, n), u2: canal(entrada.u2, n),
  };
  if (temTamanho(entrada.inclinacao)) leituras.inclinacao = canal(entrada.inclinacao, n);
  if (temTamanho(entrada.velocidade)) leituras.velocidade = canal(entrada.velocidade, n);
  if (temTamanho(entrada.tempo)) leituras.tempo = canal(entrada.tempo, n);
  return { leituras, avisos };
}

/**
 * Os avisos de uma vertical, em português: as leituras removidas (por motivo, na ordem em que
 * aparecem), a profundidade que não cresce (e onde), os trechos de u₂ negativo e as leituras que
 * chegaram sem algum canal.
 */
function avisosDaVertical(l: Leituras, removidas: LeituraRemovida[]): string[] {
  const avisos: string[] = [];

  const porMotivo = new Map<string, number>();
  for (const r of removidas) porMotivo.set(r.motivo, (porMotivo.get(r.motivo) ?? 0) + 1);
  for (const [motivo, n] of porMotivo) {
    if (motivo.startsWith('pico em ') || motivo === MOTIVO_PROFUNDIDADE_INVALIDA) {
      avisos.push(`${leiturasRemovidas(n)} por ${motivo}`);
    } else if (motivo === 'corte') {
      avisos.push(`${leiturasRemovidas(n)} por corte`);
    } else {
      avisos.push(`${leiturasRemovidas(n)} por corte (${motivo})`);
    }
  }

  // Profundidade que não cresce (repetida ou voltando): o σv soma γ·Δz, então não soma nada onde
  // Δz = 0 e SUBTRAI γ·|Δz| onde a profundidade volta; e o deslocamento e os cortes da edição supõem a
  // ordem. O aviso diz onde.
  const z = l.profundidade;
  const foraDeOrdem: number[] = [];
  for (let i = 1; i < z.length; i++) if (!(z[i] > z[i - 1])) foraDeOrdem.push(i);
  if (foraDeOrdem.length > 0) {
    const quantas = foraDeOrdem.length === 1 ? '1 leitura' : `${inteiro(foraDeOrdem.length)} leituras`;
    const onde = foraDeOrdem
      .slice(0, PROFUNDIDADES_ESCRITAS)
      .map((i) => `${metros(z[i])} m depois de ${metros(z[i - 1])} m`);
    const resto = foraDeOrdem.length - onde.length;
    avisos.push(`profundidade não crescente em ${quantas} (o σv não soma onde a profundidade se repete e subtrai `
      + `onde ela volta): `
      + `${lista(resto > 0 ? [...onde, `mais ${inteiro(resto)}`] : onde)}`);
  }

  // u₂ negativo: trechos separados por até 5 cm são um só; os mais extensos vêm escritos (no empate,
  // o mais raso primeiro), em ordem de profundidade, e o resto vai contado.
  const trechos: [number, number][] = [];
  for (let i = 0; i < l.u2.length; i++) {
    if (!(l.u2[i] < 0)) continue;
    const ultimo = trechos[trechos.length - 1];
    if (ultimo && z[i] - z[ultimo[1]] <= JUNTA_DOS_TRECHOS + FOLGA) ultimo[1] = i;
    else trechos.push([i, i]);
  }
  const extensao = (t: [number, number]): number => Math.round((z[t[1]] - z[t[0]]) * 1e6);
  const maiores = [...trechos].sort((a, b) => extensao(b) - extensao(a) || a[0] - b[0]).slice(0, TRECHOS_ESCRITOS);
  for (const [de, ate] of maiores.sort((a, b) => a[0] - b[0])) {
    avisos.push(de === ate ? `u₂ negativo em ${metros(z[de])} m` : `u₂ negativo entre ${metros(z[de])} e ${metros(z[ate])} m`);
  }
  const resto = trechos.length - TRECHOS_ESCRITOS;
  if (resto > 0) avisos.push(`u₂ negativo em mais ${resto} ${resto === 1 ? 'trecho' : 'trechos'}`);

  // Leituras sem algum canal (o −9999 do equipamento, a ponta do deslocamento): ficam, sem as contas.
  const incompletas: number[] = [];
  for (let i = 0; i < l.profundidade.length; i++) {
    if (Number.isNaN(l.qc[i]) || Number.isNaN(l.fs[i]) || Number.isNaN(l.u2[i])) incompletas.push(i);
  }
  if (incompletas.length > 0) {
    const quantas = incompletas.length === 1 ? '1 leitura' : `${inteiro(incompletas.length)} leituras`;
    const onde = incompletas.length <= PROFUNDIDADES_ESCRITAS
      ? `: ${lista(incompletas.map((i) => metros(l.profundidade[i])))} m`
      : '';
    avisos.push(`${quantas} sem qc, fs ou u₂${onde}`);
  }
  return avisos;
}

/**
 * A interpretação de uma vertical: tira as leituras sem profundidade (NaN ou infinita — elas
 * envenenariam o σv de todas as de baixo), aplica a edição e calcula o básico, a classificação e as
 * correlações. As premissas que faltam (um JSON antigo) valem o padrão; as efetivas voltam no
 * resultado (`premissas`, cópia nova), para a homologação (`grandezaHomologada(g, r.premissas)`). As
 * leituras de entrada não mudam; as removidas vêm com o índice na série ORIGINAL, em ordem. Nunca
 * lança: o que não existe é NaN.
 */
export function processarVertical(x: { leituras: Leituras; premissas: Premissas; edicao?: Edicao | null }): Resultado {
  const p = premissasEfetivas(x.premissas);
  const lidas = lerCanais(x.leituras);
  const bruto = lidas.leituras;

  // 0. A profundidade inválida sai antes da edição (os cortes e o deslocamento leem a profundidade).
  const validas: number[] = [];
  const invalidas: LeituraRemovida[] = [];
  for (let i = 0; i < bruto.profundidade.length; i++) {
    if (Number.isFinite(bruto.profundidade[i])) validas.push(i);
    else invalidas.push({ ordem: i, z: NaN, motivo: MOTIVO_PROFUNDIDADE_INVALIDA });
  }
  let entrada: Leituras = bruto; // já é cópia (lerCanais)
  if (invalidas.length > 0) {
    entrada = {
      profundidade: escolher(bruto.profundidade, validas),
      qc: escolher(bruto.qc, validas),
      fs: escolher(bruto.fs, validas),
      u2: escolher(bruto.u2, validas),
    };
    if (bruto.inclinacao) entrada.inclinacao = escolher(bruto.inclinacao, validas);
    if (bruto.velocidade) entrada.velocidade = escolher(bruto.velocidade, validas);
    if (bruto.tempo) entrada.tempo = escolher(bruto.tempo, validas);
  }

  // 1. A edição (sobre cópia); o `ordem` dela volta a ser o da série original.
  const editada = aplicarEdicao(entrada, x.edicao);
  const removidas = [
    ...invalidas,
    ...editada.removidas.map((r) => ({ ordem: validas[r.ordem], z: r.z, motivo: r.motivo })),
  ].sort((a, b) => a.ordem - b.ordem);

  // 2 a 4. O básico, a classificação e as correlações.
  const l = editada.leituras;
  const b = calcularBasico(l, p);
  const zonas = classificar(b, p);
  const c = correlacionar(b, zonas, p);

  const col: Record<Grandeza, Serie> = {
    z: b.z, qc: b.qc, fs: b.fs, u2: b.u2, qt: b.qt, rf: b.rf, gama: b.gama, sv: b.sv, u0: b.u0, svl: b.svl,
    qt1: b.qt1, fr: b.fr, bq: b.bq, n: b.n, cn: b.cn, ic: b.ic, qtn: b.qtn, icSbt: b.icSbt, U2: b.U2, ib: b.ib,
    k: c.k, ch: c.ch, n60: c.n60, n160: c.n160, m: c.m, dr: c.dr, phi: c.phi, es: c.es, g0: c.g0, nkt: c.nkt,
    su: c.su, suRazao: c.suRazao, kocr: c.kocr, ocr: c.ocr, vs: c.vs, psi: c.psi, k0: c.k0, st: c.st,
    phiPico: c.phiPico, resistencia: c.resistencia, kc: c.kc, qtncs: c.qtncs, suR: c.suR, w: c.w, e: c.e,
    gamaD: c.gamaD, porosidade: c.porosidade,
  };
  return {
    versao: MOTOR_CPTU_VERSAO,
    n: b.total,
    col,
    sbt: zonas.sbt,
    sbtn: zonas.sbtn,
    sbtnMod: zonas.sbtnMod,
    schneider: zonas.schneider,
    removidas,
    avisos: [...avisosDasCondicoes(p), ...avisosDoNa(p), ...lidas.avisos, ...avisosDaVertical(l, removidas)],
    premissas: premissasEfetivas(p), // cópia nova: quem a altera não altera o cálculo
  };
}

/**
 * O aviso das premissas fora das condições homologadas (as que o gabarito não exercita: a conformidade,
 * `condicoesAtendidas`); nenhum se a interpretação está dentro delas.
 */
function avisosDasCondicoes(p: Premissas): string[] {
  const c = condicoesAtendidas(p);
  return c.atende ? [] : [`Premissas fora das condições homologadas do motor ${MOTOR_CPTU_VERSAO}: ${c.fora.join('; ')}`];
}

/**
 * O aviso do NA sem número finito: com NaN o u₀ hidrostático fica sem valor, com infinito ele é 0 em
 * toda a vertical, e nos dois casos o w, o e, o γd e a porosidade (Barounis & Philpot, abaixo do NA).
 */
const AVISO_NA_SEM_NUMERO = 'NA sem número finito: o u₀ hidrostático e tudo o que depende do NA (o σ\'v e o que vem '
  + 'dele; w, e, γd e porosidade) não valem';

/** O aviso das premissas com o NA sem número; nenhum com o NA finito. */
function avisosDoNa(p: Premissas): string[] {
  return Number.isFinite(p.na) ? [] : [AVISO_NA_SEM_NUMERO];
}

/** O aviso da vertical sem NA na campanha nem nela: vale o do padrão, 0 — o NA na superfície. */
const AVISO_SEM_NA = 'NA não informado na vertical nem na campanha: valeu 0 (o NA na superfície, u₀ hidrostático desde '
  + 'z = 0)';

/**
 * A camada de premissas informa o NA: um número finito. `null` e `undefined` não apagam o de baixo; o
 * NaN apaga, e o NA da vertical fica sem número (`processarVertical` avisa).
 */
const informaNa = (p: Partial<Premissas> | null | undefined): boolean => Number.isFinite(p?.na);

/**
 * As interpretações das verticais de uma campanha, por código: as premissas de cada uma são o padrão,
 * coberto pelas da campanha, cobertas pelas da vertical (`premissasEfetivas`). Um código repetido fica
 * com a primeira vertical, e o resultado dela avisa. A vertical sem NA na campanha nem nela também
 * avisa: o NA do padrão é 0, e calcular com o NA na superfície sem ninguém ter dito é erro fácil de
 * não ver.
 */
export function processarCampanha(x: {
  verticais: { codigo: string; leituras: Leituras; premissas: Partial<Premissas>; edicao?: Edicao | null }[];
  premissas: Partial<Premissas>;
}): Record<string, Resultado> {
  // Sem protótipo: o código é dado de quem digita, e «__proto__» é uma vertical como outra qualquer.
  const resultados: Record<string, Resultado> = Object.create(null);
  const repetidos = new Set<string>();
  for (const v of x.verticais) {
    if (Object.prototype.hasOwnProperty.call(resultados, v.codigo)) {
      repetidos.add(v.codigo);
      continue;
    }
    resultados[v.codigo] = processarVertical({
      leituras: v.leituras, premissas: premissasEfetivas(x.premissas, v.premissas), edicao: v.edicao,
    });
    // Sem NA finito em nenhuma das duas, valeu o 0 do padrão — a menos que um NaN tenha apagado o
    // padrão, e aí o aviso é o do NA sem número, que `processarVertical` já deu.
    const r = resultados[v.codigo];
    if (!informaNa(x.premissas) && !informaNa(v.premissas) && Number.isFinite(r.premissas.na)) r.avisos.push(AVISO_SEM_NA);
  }
  for (const codigo of repetidos) {
    resultados[codigo].avisos.push(`a vertical ${codigo} aparece mais de uma vez na campanha; valeu a primeira`);
  }
  return resultados;
}
